import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Search, Filter, Wrench, CheckCircle2, Clock, AlertTriangle, X, Loader2, Home, Euro, Trash2 } from 'lucide-react';
import { MaintenanceTask, TaskPriority, TaskStatus, Property } from '../types';
import { useAuth } from '../hooks/useAuth';
import { useTasksQuery, usePropertiesQuery, useAddTaskMutation, useUpdateTaskMutation, useDeleteTaskMutation } from '../hooks/useAppQuery';

// Form Validation
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { maintenanceSchema, MaintenanceFormData } from '../lib/schemas';

interface MaintenanceProps {
  lang: 'en' | 'pt';
}

const MotionDiv = motion.div as any;

const Maintenance: React.FC<MaintenanceProps> = ({ lang }) => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Data Hooks (Autonomous)
  const { data: tasks = [], isLoading: tasksLoading } = useTasksQuery(user?.id);
  const { data: properties = [] } = usePropertiesQuery(user?.id);
  
  const addMutation = useAddTaskMutation(user?.id);
  const updateMutation = useUpdateTaskMutation(user?.id);
  const deleteMutation = useDeleteTaskMutation(user?.id);

  // Form
  const { register, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } = useForm<MaintenanceFormData>({
    resolver: zodResolver(maintenanceSchema) as any,
    defaultValues: {
      priority: TaskPriority.Medium,
      status: TaskStatus.Pending,
      costEstimate: 0
    }
  });

  const handleOpenModal = (task?: MaintenanceTask) => {
    if (task) {
      setEditingId(task.id);
      setValue('title', task.title);
      setValue('description', task.description);
      setValue('propertyId', task.propertyId);
      setValue('priority', task.priority);
      setValue('status', task.status);
      setValue('costEstimate', task.costEstimate || 0);
    } else {
      setEditingId(null);
      reset();
    }
    setIsModalOpen(true);
  };

  const onSubmit = async (data: MaintenanceFormData) => {
    try {
      if (editingId) {
        // Encontrar original para manter data
        const original = tasks.find(t => t.id === editingId);
        if (!original) return;
        await updateMutation.mutateAsync({ ...original, ...data });
      } else {
        await addMutation.mutateAsync(data as any);
      }
      setIsModalOpen(false);
      reset();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Delete this task?')) await deleteMutation.mutateAsync(id);
  };

  const getPropertyAddress = (id: string) => {
    return properties.find(p => p.id === id)?.address || 'Unknown Property';
  };

  const filteredTasks = tasks.filter(t => 
    t.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
    getPropertyAddress(t.propertyId).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPriorityColor = (p: TaskPriority) => {
    if (p === TaskPriority.High) return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (p === TaskPriority.Medium) return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
  };

  if (tasksLoading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-emerald-500 w-8 h-8"/></div>;

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto space-y-8 pb-24 h-full flex flex-col">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 shrink-0">
        <div>
          <h2 className="text-3xl font-bold text-white tracking-tight">Maintenance</h2>
          <p className="text-slate-400 mt-1">{filteredTasks.length} active tasks</p>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input 
              type="text" 
              placeholder="Search tasks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded-xl pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-emerald-500 w-64 transition-all"
            />
          </div>
          <button onClick={() => handleOpenModal()} className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 shadow-lg shadow-emerald-500/20 transition-all active:scale-95">
            <Plus className="w-4 h-4" /> New Task
          </button>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto min-h-0 custom-scrollbar">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-20">
          <AnimatePresence mode="popLayout">
            {filteredTasks.map((task) => (
              <MotionDiv
                key={task.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-slate-900/50 backdrop-blur-sm border border-slate-800 rounded-2xl p-5 hover:border-emerald-500/30 transition-all hover:shadow-2xl group"
              >
                <div className="flex justify-between items-start mb-3">
                  <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider border ${getPriorityColor(task.priority)}`}>
                    {task.priority}
                  </span>
                  {task.status === 'Completed' ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <Clock className="w-4 h-4 text-slate-500" />}
                </div>

                <h3 className="text-white font-bold text-lg mb-1">{task.title}</h3>
                <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-4">
                  <Home className="w-3 h-3" /> {getPropertyAddress(task.propertyId)}
                </div>

                <p className="text-slate-400 text-sm mb-4 line-clamp-2">{task.description}</p>

                <div className="flex items-center justify-between pt-4 border-t border-slate-800">
                  <div className="flex items-center gap-1.5 text-slate-300 font-mono text-sm">
                    <Euro className="w-3.5 h-3.5 text-emerald-500" /> {task.costEstimate}
                  </div>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleOpenModal(task)} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded hover:bg-slate-800">Edit</button>
                    <button onClick={() => handleDelete(task.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </MotionDiv>
            ))}
          </AnimatePresence>
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <MotionDiv 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-slate-950 border border-slate-800 rounded-2xl w-full max-w-lg shadow-2xl"
            >
              <div className="p-6 border-b border-slate-800 flex justify-between items-center">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                   <Wrench className="w-5 h-5 text-emerald-500"/>
                   {editingId ? 'Edit Task' : 'New Task'}
                </h3>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-500 hover:text-white"><X className="w-6 h-6"/></button>
              </div>
              
              <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
                <div className="space-y-1.5">
                   <label className="text-xs font-bold text-slate-400">Title</label>
                   <input {...register('title')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none" />
                   {errors.title && <span className="text-xs text-red-400">{errors.title.message}</span>}
                </div>

                <div className="space-y-1.5">
                   <label className="text-xs font-bold text-slate-400">Property</label>
                   <select {...register('propertyId')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none focus:border-emerald-500">
                      <option value="">Select Property...</option>
                      {properties.map(p => <option key={p.id} value={p.id}>{p.address}</option>)}
                   </select>
                   {errors.propertyId && <span className="text-xs text-red-400">{errors.propertyId.message}</span>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                   <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-400">Priority</label>
                      <select {...register('priority')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none">
                         {Object.values(TaskPriority).map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                   </div>
                   <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-400">Status</label>
                      <select {...register('status')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white outline-none">
                         {Object.values(TaskStatus).map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                   </div>
                </div>

                <div className="space-y-1.5">
                   <label className="text-xs font-bold text-slate-400">Estimated Cost (€)</label>
                   <input type="number" {...register('costEstimate')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none" />
                </div>

                <div className="space-y-1.5">
                   <label className="text-xs font-bold text-slate-400">Description</label>
                   <textarea {...register('description')} className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-white focus:border-emerald-500 outline-none h-24" />
                   {errors.description && <span className="text-xs text-red-400">{errors.description.message}</span>}
                </div>

                <div className="pt-4 flex justify-end gap-3">
                    <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Cancel</button>
                    <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-bold flex items-center gap-2 disabled:opacity-50">
                        {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
                        Save Task
                    </button>
                </div>
              </form>
            </MotionDiv>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Maintenance;